import { useState, useEffect, useMemo, useCallback, useRef } from 'react';

// Custom hook for debounced values
export function useDebounce<T>(value: T, delay: number): T {
    const [debouncedValue, setDebouncedValue] = useState<T>(value);

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);

        return () => {
            clearTimeout(handler);
        };
    }, [value, delay]);

    return debouncedValue;
}

// Custom hook for media queries
export function useMediaQuery(query: string): boolean {
    const [matches, setMatches] = useState(() => {
        if (typeof window !== 'undefined') {
            return window.matchMedia(query).matches;
        }
        return false;
    });

    useEffect(() => {
        if (typeof window === 'undefined') return;

        const mediaQuery = window.matchMedia(query);
        // The initial state belongs to the previous query when `query` changes.
        setMatches(mediaQuery.matches);
        const handler = (event: MediaQueryListEvent) => setMatches(event.matches);

        mediaQuery.addEventListener('change', handler);
        return () => mediaQuery.removeEventListener('change', handler);
    }, [query]);

    return matches;
}

// Custom hook for debounced search
export function useDebouncedSearch(searchFunction: (query: string) => void, delay: number = 300) {
    const debouncedFunction = useMemo(
        () => {
            let timeoutId: NodeJS.Timeout;
            return (query: string) => {
                clearTimeout(timeoutId);
                timeoutId = setTimeout(() => searchFunction(query), delay);
            };
        },
        [searchFunction, delay]
    );

    return debouncedFunction;
}

// Custom hook for element size calculations
export function useElementSize(ref: React.RefObject<HTMLElement>) {
    const [size, setSize] = useState({ width: 0, height: 0 });

    useEffect(() => {
        if (!ref.current) return;

        const resizeObserver = new ResizeObserver(entries => {
            for (const entry of entries) {
                setSize({
                    width: entry.contentRect.width,
                    height: entry.contentRect.height,
                });
            }
        });

        resizeObserver.observe(ref.current);
        return () => resizeObserver.disconnect();
    }, [ref]);

    return size;
}

// Custom hook for visible skill count calculation
export function useVisibleSkillCount(
    containerRef: React.RefObject<HTMLElement>,
    skills: any[] = [],
    isGridView: boolean = false
) {
    const { width } = useElementSize(containerRef);

    const visibleCount = useMemo(() => {
        if (width === 0 || skills.length === 0) return null;

        // Estimate based on container width and grid view
        const avgSkillWidth = isGridView ? 60 : 80; // Rough estimate
        const gap = 8; // Gap between skills
        const availableWidth = width - 32; // Account for padding

        const maxVisible = Math.floor(availableWidth / (avgSkillWidth + gap));
        return Math.min(maxVisible, skills.length);
    }, [width, skills.length, isGridView]);

    return visibleCount;
}

// Optimized throttle function
export function useThrottle<T extends (...args: any[]) => any>(
    callback: T,
    delay: number
): T {
    const lastRun = useRef<number>(Date.now());

    const throttledCallback = useCallback(
        (...args: Parameters<T>) => {
            if (Date.now() - lastRun.current >= delay) {
                callback(...args);
                lastRun.current = Date.now();
            }
        },
        [callback, delay]
    ) as T;

    return throttledCallback;
}

// Performance-optimized equality checker for arrays
export function useShallowEqual<T>(value: T[]): T[] {
    const ref = useRef<T[]>(value);

    const isEqual = useMemo(() => {
        if (value.length !== ref.current.length) {
            ref.current = value;
            return false;
        }

        for (let i = 0; i < value.length; i++) {
            if (value[i] !== ref.current[i]) {
                ref.current = value;
                return false;
            }
        }

        return true;
    }, [value]);

    return isEqual ? ref.current : value;
}
